import PropTypes from 'prop-types';
import React from 'react';
import Validator from 'mixins/validator';
import Resetter from 'mixins/resetter';
import { IconButton } from 'react-toolbox/lib/button';
import { Dialog } from 'react-toolbox';
import TextArea from 'components/TextArea';
import MessageBox from 'components/MessageBox';
import ColoredButton from 'components/ColoredButton';
import PhaseType from 'types/phase';
import TaskType from 'types/task';
import { errorSummary } from '../../../../../../../../../utils';
import modalStyles from 'modal.scss';
import ConfirmDialog from 'components/ConfirmDialog';
import JobPaperworkWarning from '../JobPaperworkWarning';
import { requestJobPaperworkTemplates } from '../../../../../../../../../shared/actionCreators';
import isIfiPhase from '../../../ifiUtils';
import {
  CUSTOM_WORK_CONFIRM_BODY,
  CUSTOM_WORK_CONFIRM_TITLE_NEEDS_CUSTOM,
  CUSTOM_WORK_CONFIRM_TITLE_NO_CUSTOM,
  phaseHasCustomWork,
} from '../../../jobPaperworkTemplateTypes';

class CompleteTaskModal extends Resetter(Validator(React.Component)) {
  static DEFAULT_STATE = {
    note: '',
    errors: {},
    sendError: false,
    templates: [],
    templatesLoaded: false,
    templatesLoadError: false,
    checked: false,
    showCustomWorkConfirm: false,
    customWorkNeeded: false,
    pendingCompletion: null,
  }
  static VALIDATIONS = { note: 'This field is required' }

  componentDidMount = () => {
    if (this.props.active && this.shouldLoadTemplates()) this.loadTemplates();
  }

  componentDidUpdate = (prevProps) => {
    const becameActive = !prevProps.active && this.props.active;
    const { phase, task: { slug } } = this.props;
    const relevantPhaseChanged = prevProps.phase.id !== phase.id;
    const relevantTaskChanged = prevProps.task.slug !== slug;

    if (relevantPhaseChanged || relevantTaskChanged) {
      this.setState({
        showCustomWorkConfirm: false,
        customWorkNeeded: false,
        pendingCompletion: null,
      });
    }

    if (
      this.props.active &&
      (becameActive || relevantPhaseChanged || relevantTaskChanged) &&
      this.shouldLoadTemplates()
    ) {
      this.loadTemplates();
    }
  }

  shouldLoadTemplates = () => {
    const { phase, task: { slug } } = this.props;
    const { type } = phase;
    return (slug === 'workorder_complete' || slug === 'measure') &&
      (type === 'Assessment' || type === 'Workorder') &&
      isIfiPhase(phase);
  }

  shouldConfirmCustomWork = slug => slug === 'measure';

  loadTemplates = () => {
    const { phase } = this.props;
    this.setState({ templates: [], templatesLoaded: false, templatesLoadError: false });
    requestJobPaperworkTemplates(phase.id)
      .then(({ templates, error }) => {
        if (error) {
          this.setState({ templatesLoaded: true, templatesLoadError: true });
          return;
        }
        this.setState({
          templates,
          templatesLoaded: true,
          templatesLoadError: false,
        });
      });
  }

  phasePermitRequired = () => {
    const { phase } = this.props;
    return !!(phase && phase.phase_specific &&
              phase.phase_specific.millwork_specifics &&
              phase.phase_specific.millwork_specifics.permit_required);
  }

  data = () => ({ event: { data: { note: this.state.note } } });

  completeTask = ({ phase, slug, updatePhaseTask, childHandleTasksClose }) => {
    const task = { status: 'complete' };
    const data = this.data();
    updatePhaseTask(
      phase.id,
      slug,
      { task, ...data },
    ).then(() => childHandleTasksClose()).then(() => this.handleClose())
      .catch(result => this.setState({ sendError: true, errors: result.payload.errors }));
  }

  handleSubmit = () => {
    const { phase, task: { slug }, updatePhaseTask, childHandleTasksClose } = this.props;
    if (!this.validate()) return;

    const completionContext = { phase, slug, updatePhaseTask, childHandleTasksClose };

    if (this.shouldLoadTemplates()) {
      requestJobPaperworkTemplates(phase.id)
        .then(({ templates, error }) => {
          if (error) {
            this.setState({
              sendError: true,
              errors: {
                templates: ['Unable to load job paperwork templates. Please try again.'],
              },
            });
            return;
          }

          this.setState({ templates, templatesLoaded: true, templatesLoadError: false });

          const validationError = JobPaperworkWarning.validate(templates, slug);
          if (validationError) {
            this.setState({
              sendError: true,
              errors: {
                templates: [validationError],
              },
            });
            return;
          }

          if (this.shouldConfirmCustomWork(slug)) {
            this.setState({
              showCustomWorkConfirm: true,
              customWorkNeeded: phaseHasCustomWork(templates),
              pendingCompletion: completionContext,
              sendError: false,
            });
            return;
          }

          this.completeTask(completionContext);
        });
      return;
    }

    this.completeTask(completionContext);
  }

  handleCustomWorkConfirmProceed = () => {
    const { pendingCompletion } = this.state;
    this.setState({ showCustomWorkConfirm: false });
    if (!pendingCompletion) return;
    if (!this.validate()) return;
    this.completeTask(pendingCompletion);
  }

  handleCustomWorkConfirmCancel = () => this.handleClose();

  handleNoteChange = e => this.setState({ note: e.target.value });

  handlePermitCheckChange = e => this.setState({ checked: e.target.checked });

  render(children = null, errorMessage = null) {
    const phasePermitRequired = this.phasePermitRequired();
    const { active, task } = this.props;
    const { note, errors, sendError } = this.state;
    const noteLabel = this.constructor.NOTE_LABEL || `Notes ${this.constructor.VALIDATIONS.note ? '*' : ''}`;
    const shouldLoadTemplates = this.shouldLoadTemplates();
    return (<Dialog
      active={active}
      className={modalStyles.modal}
      type="fullscreen"
      onEscKeyDown={this.handleClose}
      onOverlayClick={this.handleClose}
    >
      <IconButton
        icon="close"
        className={modalStyles.close}
        onClick={this.handleClose}
        tabIndex={-1}
      />
      <h5 className={modalStyles.title}>Complete Task: {task.name}</h5>
      {(task.slug === 'workorder_complete' || task.slug === 'measure') &&
       (this.props.phase.type === 'Assessment' || this.props.phase.type === 'Workorder') &&
       isIfiPhase(this.props.phase) && (
         <JobPaperworkWarning
           templates={this.state.templates}
           templatesLoaded={this.state.templatesLoaded}
           templatesLoadError={this.state.templatesLoadError}
           taskSlug={task.slug}
           permitRequired={phasePermitRequired}
         />
       )}
      {shouldLoadTemplates && phasePermitRequired && task.slug !== 'measure' && <React.Fragment>
        <input
          type="checkbox"
          id="permit-uploaded-checkbox"
          name="permit-uploaded"
          value="yes"
          checked={this.state.checked}
          onChange={this.handlePermitCheckChange}
        />
        <label
          htmlFor="permit-uploaded-checkbox"
          className={modalStyles.imsCheckboxLabel}
        >
          Permits were uploaded to VG
        </label>
        <br />
      </React.Fragment>}
      <TextArea
        label={noteLabel}
        value={note}
        errors={errors.note}
        onChange={this.handleNoteChange}
        autoFocus
      />
      {errorMessage && <MessageBox error message={errorMessage} />}
      {sendError && !errorMessage && (
        <MessageBox error message={errorSummary(errors.templates || errors)} />
      )}
      {task.name !== 'Complete Verification' && children}
      <div className={modalStyles.actions}>
        <ColoredButton
          color="green"
          responsive
          label="Complete Task"
          className={modalStyles.button}
          onClick={this.handleSubmit}
          disabled={shouldLoadTemplates && (
            !!JobPaperworkWarning.validate(this.state.templates, task.slug) ||
            (phasePermitRequired && !this.state.checked && task.slug !== 'measure')
          )}
        />
        <ColoredButton
          color="darkGray"
          responsive
          label="Cancel"
          className={modalStyles.button}
          onClick={this.handleClose}
        />
      </div>
      <ConfirmDialog
        title={this.state.customWorkNeeded
          ? CUSTOM_WORK_CONFIRM_TITLE_NEEDS_CUSTOM
          : CUSTOM_WORK_CONFIRM_TITLE_NO_CUSTOM}
        confirmation={CUSTOM_WORK_CONFIRM_BODY}
        show={this.state.showCustomWorkConfirm}
        proceed={this.handleCustomWorkConfirmProceed}
        cancel={this.handleCustomWorkConfirmCancel}
        dismiss={this.handleCustomWorkConfirmCancel}
      />
    </Dialog>);
  }
}

CompleteTaskModal.propTypes = {
  active: PropTypes.bool.isRequired,
  phase: PhaseType.isRequired,
  task: TaskType.isRequired,
  onClose: PropTypes.func.isRequired,
  updatePhaseTask: PropTypes.func.isRequired,
  childHandleTasksClose: PropTypes.func.isRequired,
};

CompleteTaskModal.defaultProps = { active: false };

export default CompleteTaskModal;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/tasks/components/CompleteTaskModal/index.jsx